import { useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- NiceModal -> overlay-kit migration is out of scope for this PR
import NiceModal from '@ebay/nice-modal-react';
import { DehydratedState, Hydrate, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { crmUserContext } from '@zep-us/cs/user-context';
import { createHelpCenterUserInfo } from '@zep-us/cs/user-info';
import I18NextChainedBackend from 'i18next-chained-backend';
import { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { appWithTranslation, UserConfig } from 'next-i18next';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';
import { OverlayProvider } from 'overlay-kit';
import '../init';
import '../styles/globals.scss';
import '../styles/motions.scss';
import '@zep-us/cs/styles';
import 'react-loading-skeleton/dist/skeleton.css';
import { defaultAxiosConfig, QUERY_KEY_CMS, ZepApiProvider } from '@zep/apis';
import { AuthProvider, useUser } from '@zep/auth';
import { ZEM_CHARGE_POPUP_MODE } from '@zep/common/routes';
import { logger } from '@zep/core-logger';
import { chainedBackendOptions } from '@zep/i18n';
import { DevToolingMount } from '@zep/internal';
import { PageWithLayout, User } from '@zep/types';
import { isApp, isClient, isEuropeByTimeZone } from '@zep/utils';
import nextI18nConfig from '../../next-i18next.config';
import { getDefaultLayout } from '../components/layouts/DefaultLayout';
import RootLayout from '../components/layouts/RootLayout';
import Meta, { MetaProps } from '../components/service/common/Meta';
import { WebAppLoggerInitializer } from '../components/WebAppLoggerInitializer';
import { CHANNEL_TALK_UNUSED_PATHS } from '../consts';
import GlobalErrorBoundary from '../guards/GlobalErrorBoundary';
import { isDevelopment, isLocal, isProduction } from '../utils';
const HelpCenterWidget = dynamic(() => import('@zep-us/cs').then(mod => mod.HelpCenterWidget), {
  ssr: false
});

// CRM 공지는 첫 화면 렌더에 필요하지 않으므로 별도 청크에서 클라이언트에만 마운트한다.
const CrmWithAuth = dynamic(() => import('../components/crm/CrmWithAuth').then(mod => mod.CrmWithAuth), {
  ssr: false
});
const DEFAULT_DEV_HELPCENTER_CDN_URL = 'https://cdn-static-dev-1-crm.zep.us';

// design mode 전용 인스펙터. next/dynamic + ssr:false 로 별도 청크 분리해
// PRD 초기 번들에서 제외(렌더는 아래 NEXT_PUBLIC_STAGE 가드).
const DesignInspector = dynamic(() => import(/* webpackChunkName: "design-inspector" */'@zep/internal').then(m => ({
  default: m.DesignInspector
})), {
  ssr: false
});
const ZepApp = ({
  Component,
  pageProps
}: ZepAppProps) => {
  const {
    seoContent,
    serverUser
  } = pageProps;
  const router = useRouter();
  const isCookiePanelEnabled = !router.pathname.includes('/web3');
  const [isShowCookiePanel, setShowCookiePanel] = useState(() => shouldShowCookiePanelInitially(isCookiePanelEnabled));
  const [queryClient] = useState(() => new QueryClient({
    // 비필수 CMS/weegloo 콘텐츠 쿼리는 useErrorBoundary:false 라 GlobalErrorBoundary 로
    // 에러가 올라가지 않는다(= 페이지 안 죽음). 그 대신 여기서 handled 로 Sentry 에 남겨
    // 백엔드 500(WEB500007 SpelEvaluationException 등)에 대한 관측성을 유지한다.
    // QueryCache onError 는 useErrorBoundary 값과 무관하게 모든 쿼리 에러에 발화한다.
    queryCache: new QueryCache({
      onError: (error, query) => {
        const key = query.queryKey?.[0];
        if (key === QUERY_KEY_CMS) {
          logger.captureError(error, {
            level: 'warning',
            fingerprint: `cms-content-error-${String(key)}`,
            tags: {
              source: 'cms-content'
            }
          });
        }
      }
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchIntervalInBackground: false,
        keepPreviousData: true,
        useErrorBoundary: true,
        retry: false,
        networkMode: 'always'
      },
      mutations: {
        networkMode: 'always'
      }
    }
  }));
  const getLayout = Component.getLayout ?? getDefaultLayout;
  const isZemChargePopup = useMemo(() => {
    if (router.pathname !== '/accounts/charge') return false;
    const queryString = router.asPath.split('?')[1]?.split('#')[0] ?? '';
    return new URLSearchParams(queryString).get('mode') === ZEM_CHARGE_POPUP_MODE;
  }, [router.asPath, router.pathname]);
  // 플로팅 위젯(헬프센터·CRM 공지) 공통 차단 조건. 두 위젯의 조건이 따로 놀며
  // 드리프트하지 않도록 공유 부분을 한 변수로 묶는다.
  const isChannelTalkUnusedPath = CHANNEL_TALK_UNUSED_PATHS.some(hidePath => router.pathname.startsWith(hidePath));
  const isFloatingWidgetBlocked = isShowCookiePanel || isZemChargePopup || isChannelTalkUnusedPath || isClient() && window.top !== window.self;
  const shouldHideHelpCenterWidget = isFloatingWidgetBlocked;
  // CRM 공지는 추가로 마케팅 쿠키 거부(necessary) 유저에게 띄우지 않는다.
  // 약관 동의(/accounts/agree-terms) 같은 blocking 온보딩 플로우와 /shared
  // 임베드는 isChannelTalkUnusedPath 로 헬프센터와 동일하게 차단된다.
  const shouldHideCrmNotices = isFloatingWidgetBlocked || isClient() && getAcceptCookieValue() === 'necessary';
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_STAGE === 'LOC') return;
    const {
      hostname,
      pathname
    } = window.location;
    if (!hostname.endsWith('.zep.us') && hostname !== 'zep.us') {
      window.location.href = `https://zep.us${pathname}`;
    }
  }, []);
  return <>
      {process.env.NEXT_PUBLIC_STAGE !== 'PRD' && <DesignInspector />}
      <DevToolingMount data-sentry-element="DevToolingMount" data-sentry-source-file="_app.tsx" />
      <Meta partialSeoContent={seoContent} data-sentry-element="Meta" data-sentry-source-file="_app.tsx" />
      <GlobalErrorBoundary data-sentry-element="GlobalErrorBoundary" data-sentry-source-file="_app.tsx">
        <WebAppLoggerInitializer data-sentry-element="WebAppLoggerInitializer" data-sentry-source-file="_app.tsx" />
        <NuqsAdapter data-sentry-element="NuqsAdapter" data-sentry-source-file="_app.tsx">
          <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-source-file="_app.tsx">
            <Hydrate state={pageProps.dehydratedState} data-sentry-element="Hydrate" data-sentry-source-file="_app.tsx">
              <ZepApiProvider axiosConfig={defaultAxiosConfig} data-sentry-element="ZepApiProvider" data-sentry-source-file="_app.tsx">
                <AuthProvider initialUser={serverUser} data-sentry-element="AuthProvider" data-sentry-source-file="_app.tsx">
                  <RootLayout onCookiePanelVisibilityChange={setShowCookiePanel} data-sentry-element="RootLayout" data-sentry-source-file="_app.tsx">
                    <NiceModal.Provider data-sentry-element="unknown" data-sentry-source-file="_app.tsx">
                      <OverlayProvider data-sentry-element="OverlayProvider" data-sentry-source-file="_app.tsx">
                        {getLayout(<Component {...pageProps} />)}
                        {/* 마운트 게이팅 대신 suspended 전달: 쿠키 패널 토글 등으로
                            언마운트되면 노출 이력이 유실돼 같은 공지가 재노출된다.
                            ErrorBoundary 는 dynamic chunk 로드 실패(배포 직후 stale
                            404 등)가 GlobalErrorBoundary 까지 올라가 페이지 전체가
                            500 화면으로 대체되는 것을 막는다. */}
                        <ErrorBoundary fallback={null} onError={error => logger.captureError(error, {
                        tags: {
                          source: 'CrmWithAuth'
                        }
                      })} data-sentry-element="ErrorBoundary" data-sentry-source-file="_app.tsx">
                          <CrmWithAuth suspended={shouldHideCrmNotices} data-sentry-element="CrmWithAuth" data-sentry-source-file="_app.tsx" />
                        </ErrorBoundary>
                      </OverlayProvider>
                    </NiceModal.Provider>
                  </RootLayout>
                  {isDevelopment && <ReactQueryDevtools initialIsOpen={false} />}
                  {!shouldHideHelpCenterWidget && <HelpCenterWidgetWeb locale={router.locale as 'ko' | 'en' | 'ja' ?? 'ko'} />}
                </AuthProvider>
              </ZepApiProvider>
            </Hydrate>
          </QueryClientProvider>
        </NuqsAdapter>
      </GlobalErrorBoundary>
    </>;
};
const clientI18nConfig: UserConfig = {
  ...nextI18nConfig,
  use: [I18NextChainedBackend],
  backend: chainedBackendOptions('web')
};
export default appWithTranslation(ZepApp, isClient() ? clientI18nConfig : nextI18nConfig);
type ZepAppProps = AppProps<{
  seoContent?: MetaProps['partialSeoContent'];
  serverUser?: User;
  dehydratedState?: DehydratedState;
}> & {
  Component: PageWithLayout;
};
function HelpCenterWidgetWeb({
  locale
}: {
  locale: 'ko' | 'en' | 'ja';
}) {
  const {
    user
  } = useUser();
  const apiBaseUrl = resolveHelpCenterApiBaseUrl();
  const cdnBaseUrl = resolveHelpCenterCdnBaseUrl();
  const hasMissingConfig = !apiBaseUrl || !cdnBaseUrl;
  useEffect(() => {
    if (!isProduction || !hasMissingConfig) return;
    logger.captureError(new Error('helpcenter_config_missing'), {
      attributes: {
        source: 'HelpCenterWidget',
        app: 'web-app',
        missingApiBaseUrl: String(!apiBaseUrl),
        missingCdnBaseUrl: String(!cdnBaseUrl),
        stage: process.env.NEXT_PUBLIC_STAGE ?? ''
      }
    });
  }, [apiBaseUrl, cdnBaseUrl, hasMissingConfig]);
  if (hasMissingConfig) return null;
  return <HelpCenterWidget apiBaseUrl={apiBaseUrl} cdnBaseUrl={cdnBaseUrl} appId="helpdesk-web" service="core" locale={locale}
  // CRM has a dedicated inquiry workflow route keyed by this virtual path.
  urlPath="/inquiry" showSearch={false} showHelpCategories={false} userInfo={toWebHelpCenterUserInfo(user)} onError={error => logger.captureError(error, {
    attributes: {
      source: 'HelpCenterWidget'
    }
  })} data-sentry-element="HelpCenterWidget" data-sentry-component="HelpCenterWidgetWeb" data-sentry-source-file="_app.tsx" />;
}
function toWebHelpCenterUserInfo(user: User | null | undefined) {
  return createHelpCenterUserInfo(user, user => ({
    id: user.hashId,
    email: user.snsId,
    name: user.name,
    context: {
      ...crmUserContext({
        country_code: user.countryCode
      }),
      is_edu: user.isEdu,
      gm_level: user.gmLevel,
      sns_id: user.snsId
    }
  }));
}
function resolveHelpCenterApiBaseUrl() {
  if (isLocal) return '/crm';
  return process.env.NEXT_PUBLIC_CRM_URL ?? null;
}
function resolveHelpCenterCdnBaseUrl() {
  if (process.env.NEXT_PUBLIC_HELPCENTER_CDN_URL) {
    return process.env.NEXT_PUBLIC_HELPCENTER_CDN_URL;
  }
  if (process.env.NEXT_PUBLIC_STAGE !== 'PRD') {
    return DEFAULT_DEV_HELPCENTER_CDN_URL;
  }
  return null;
}
function shouldShowCookiePanelInitially(enabledCookiePanel: boolean) {
  if (!enabledCookiePanel || !isClient() || isApp()) return false;
  return getAcceptCookieValue() === 'unknown' && isEuropeByTimeZone();
}
function getAcceptCookieValue() {
  const acceptCookie = document.cookie.split(';').map(cookie => cookie.trim()).find(cookie => cookie.startsWith('AcceptCookie='))?.split('=')[1];
  if (acceptCookie === 'all' || acceptCookie === 'necessary') {
    return acceptCookie;
  }
  return 'unknown';
}